// Dependencies
import React, { useState } from 'react';

// Utils
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import { assign, compact, get, has, set } from 'lodash';
import { connect } from 'react-redux';

import { Impression } from '@nerdwallet/analytics/react/impression';
import {
  AnalyticsPageView,
  AnalyticsProvider,
} from '@nerdwallet/seo-analytics';
import { Button } from '@nerdwallet/currency';
import { getMacroKey } from '@nerdwallet/react-config-builder';
import { SECTION_NAME } from '../../lib/analytics-constants';
import lazy, { Hydrate } from '../../components/lazy/lazy-universal';

// Actions
// Local Components
import CardCta from '../../components/currency/card-cta/card-cta';
import Results from '../../components/currency/results/results';
import ToolLinkCards from '../../components/currency/tool-link-cards/tool-link-cards';

// Configs and Data
import { RELATED_ARTICLES } from '../../components/refinance-calculator/analytics-constants';
import inputLibrary from '../../components/refinance-calculator/input-library';
import OnboardingModal from '../../components/refinance-calculator/onboarding-modal';
import RefiControlPanelView from '../../components/refinance-calculator/rc-control-panel-view/control-panel-view';
import detailsTableConfig from '../../components/refinance-calculator/rc-results-panel/details-table/rc-details-table-accordion-config';
import RCResultsPanel from '../../components/refinance-calculator/rc-results-panel/rc-results-panel';
import RcToolLinkCards from '../../components/refinance-calculator/rc-tool-link-cards';
import { useUrlRecordContext } from '../../components/url-record';
import { overridesChange } from '../../lib/actions';
import inputChange from '../../lib/actions/inputChange';
import { ReducerInputsData, RefinanceCalculatorField } from '../../lib/utils';
import { Browser } from '../../types';

// @ts-ignore - ignore error
import styles from './rc-results.module.less';

// Lazy Load Components
const LenderCards = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-lender-cards' */ '../../components/currency/lender-cards/lender-cards'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const RelatedArticles = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-related-articles' */ '../../components/currency/related-articles/related-articles'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const Methodology = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-methodology' */ '../../components/currency/methodology/methodology'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

type Data = {
  hasCompletedOnboardingRc?: boolean;
};

type Validations = Pick<
  ReducerInputsData<RefinanceCalculatorField>,
  'validations'
>;
export interface RCResultsProps {
  app?: string;
  dispatch: (any) => void;
  refinanceCalculator?: Record<string, any>;
  history: Record<string, any>;
  identity: Record<string, any>;
  location: Record<string, any>;
  validations?: Validations;
  browser: Browser;
  onboardingBypass: boolean;
  route: Record<string, any>;
  homeCalculatorProfile?: {
    data: Data;
  };
}

const RCResults = (props: RCResultsProps): JSX.Element => {
  const {
    browser,
    location,
    history,
    homeCalculatorProfile,
    identity,
    onboardingBypass,
    route,
    refinanceCalculator,
    validations,
  } = props;
  const data = {
    ...refinanceCalculator,
    validations,
  };
  const [showOnboardingModal, setShowOnboardingModal] = useState(
    !onboardingBypass
  );
  const { title, synopsis } = useUrlRecordContext();

  const onChange = (config, value) => {
    const key = getMacroKey(config, 'value');
    const values = set(
      {
        name: 'refinanceCalculator',
      },
      key,
      value
    );

    // Apply the right action based on what the key is.
    const allInputs = config.additionalInputIdsToValidate
      ? config.additionalInputIdsToValidate
          .map((input) => inputLibrary[input])
          .concat(inputLibrary[config.inputId])
      : [inputLibrary[config.inputId]];

    if (has(values, 'overrides')) {
      props.dispatch(overridesChange(values, compact(allInputs)));
    } else {
      // Gather dependent inputs i.e changing home price needs to validate down payment
      props.dispatch(inputChange(values, compact(allInputs)));
    }
  };

  const closeModal = () => {
    setShowOnboardingModal(false);
  };

  const resultsPanelProps = {
    data,
    onChange,
    detailsTableConfig,
    trackingFormName: 'maintool',
    ...props,
    isSignedIn: get(identity, 'isSignedIn', false),
  };

  return (
    <AnalyticsProvider>
      <AnalyticsPageView name="mortgages_refinance_calculator">
        <Impression
          eventType="page_impression"
          entityName={
            get(route, 'name') === 'default'
              ? 'mortgages_refinance_calculator_form'
              : 'mortgages_refinance_calculator'
          }
        >
          <OnboardingModal
            isOpen={showOnboardingModal}
            browser={browser}
            location={location}
            history={history}
            onClose={closeModal}
          />
          <Results
            {...assign({}, props, {
              className: 'rc-app',
              controlPanel: (
                <RefiControlPanelView
                  enableTracking={
                    onboardingBypass ||
                    homeCalculatorProfile?.data?.hasCompletedOnboardingRc
                  }
                />
              ),
              data,
              heading: title,
              subcopy: synopsis,
              lenders: (
                <LenderCards
                  trackingFormName="rc"
                  namedQuery="mortgages_offers_rc"
                  explanation="Lock in your rate:"
                  analyticsTrackingConfig={{
                    entityName: 'mortgages_refi_calculator_offer',
                    sectionName: 'mortgages_refi_calculator_offer',
                    sectionPosition: 2,
                  }}
                  cardsPerRow={3}
                />
              ),
              membershipCta: (
                <CardCta
                  iconName="House_pricetag"
                  title="Curious what your home is really worth?"
                  subtitle="NerdWallet lets you know what your home is worth and tracks its value for you. NerdWallet will also notify you when it thinks you may save by refinancing."
                  trackingId="refi_home_value_cta"
                >
                  <Button
                    href="/mortgages/home-value"
                    primary
                    className={styles.ctaButton}
                  >
                    Get started
                  </Button>
                </CardCta>
              ),
              resultsPanel: <RCResultsPanel {...resultsPanelProps} />,
              methodology: (
                <Methodology
                  trackingFormName="methodology"
                  impressionTracking
                />
              ),
              relatedArticles: (
                <RelatedArticles
                  entityName={RELATED_ARTICLES.entityName}
                  sectionName={RELATED_ARTICLES.sectionName}
                  sectionPosition={RELATED_ARTICLES.sectionPosition}
                  articles={RELATED_ARTICLES.articles}
                />
              ),
              toolLinkCards: (
                <ToolLinkCards
                  header="Other NerdWallet resources"
                  impressionTrackingName={
                    SECTION_NAME.MORTGAGES_REFINANCE_TOOL_CARD
                  }
                  analyticsConfig={{
                    sectionName: SECTION_NAME.MORTGAGES_REFINANCE_TOOL_CARD,
                    sectionPosition: 7,
                  }}
                  // @ts-ignore - ignore error
                  data={data}
                  cards={RcToolLinkCards}
                />
              ),
            })}
          />
        </Impression>
      </AnalyticsPageView>
    </AnalyticsProvider>
  );
};

RCResults.defaultProps = {
  app: undefined,
  refinanceCalculator: {},
  validations: {},
};

export default connect((state) => state)(RCResults);
